import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import gundi_logo from '../../images/gundi_logo.png';

// Constant for tracking intentional logout to prevent showing session expired modal
export const KEYCLOAK_INTENTIONAL_LOGOUT_KEY = 'keycloak_intentional_logout';

export function useKeycloakToken() {
  const { keycloak, initialized } = useKeycloak();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false); // Track if modal is shown due to session expiration
  const intervalIdRef = useRef<any>(null);
  const loginInProgressRef = useRef<boolean>(false);
  const refreshInProgressRef = useRef<boolean>(false);
  const onTokenExpiredHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const onAuthErrorHandlerRef = useRef<((error: any) => void) | null>(null);
  const wasAuthenticatedRef = useRef<boolean>(false);

  useEffect(() => {
    const checkSession = async () => {
      if (!initialized) {
        return;
      }

      // Check if this is an intentional logout (don't show modal for intentional logouts)
      // Use localStorage so it persists even if sessionStorage is cleared
      const isIntentionalLogout = localStorage.getItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY) === 'true';

      if (!keycloak.authenticated) {
        // Clear userAuthorized flag when session expires (not on first visit)
        if (wasAuthenticatedRef.current) {
          localStorage.removeItem("userAuthorized");
        }

        // Only show modal if:
        // 1. This is NOT an intentional logout
        // 2. User was previously authenticated (session expired, not first visit)
        // For intentional logouts or first visits, don't show modal
        if (!isIntentionalLogout && wasAuthenticatedRef.current) {
          setIsSessionExpired(false); // Keycloak session check, not API 401
          setShowLoginModal(true);
        } else {
          // Stop the interval check for intentional logout to prevent modal from showing
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          // Hide modal and don't clear flag yet (will be cleared on next login)
          // This prevents the interval from showing modal again
          setShowLoginModal(false);
        }
        return;
      }

      // Track that user was authenticated (for detecting session expiration vs first visit)
      if (keycloak.authenticated) {
        wasAuthenticatedRef.current = true;
      }

      // If user is authenticated again after logout, clear the logout flag and restart interval if needed
      const wasIntentionalLogout = localStorage.getItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY) === 'true';
      if (wasIntentionalLogout) {
        localStorage.removeItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY);
      }
      // Restart interval if it was stopped
      if (!intervalIdRef.current) {
        intervalIdRef.current = setInterval(checkSession, 10000);
      }

      // Prevent concurrent refresh attempts
      if (refreshInProgressRef.current) {
        return;
      }

      // Try to refresh the token if it's expired or about to expire
      try {
        refreshInProgressRef.current = true;
        const refreshed = await keycloak.updateToken(30); // 30s min validity

        // After updateToken, check if token is still expired
        // This handles the case where refresh token itself has expired
        if (!refreshed && keycloak.isTokenExpired()) {
          setIsSessionExpired(false); // Keycloak expiration, not API 401
          setShowLoginModal(true);
        } else {
          setShowLoginModal(false);
        }
      } catch (err) {
        console.error('Keycloak token refresh failed:', err);
        // Only show modal if we're actually authenticated but refresh failed
        // This prevents showing modal during initial auth
        if (keycloak.authenticated) {
          setIsSessionExpired(false); // Keycloak expiration, not API 401
          setShowLoginModal(true);
        }
      } finally {
        refreshInProgressRef.current = false;
      }
    };

    // Check session immediately
    checkSession();

    // Set up a periodic session check only if not an intentional logout
    // (If it's an intentional logout, the interval will be stopped in checkSession)
    const isIntentionalLogout = localStorage.getItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY) === 'true';
    if (!isIntentionalLogout && !intervalIdRef.current) {
      intervalIdRef.current = setInterval(checkSession, 10000); // every 10s
    }

    // Set up the onTokenExpired event handler
    onTokenExpiredHandlerRef.current = async () => {
      const isIntentionalLogout = localStorage.getItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY) === 'true';
      if (!initialized || loginInProgressRef.current || refreshInProgressRef.current || isIntentionalLogout) {
        return;
      }

      try {
        refreshInProgressRef.current = true;
        const refreshed = await keycloak.updateToken(30);

        // If refresh failed (refreshed === false), or token is still expired, show login modal
        if (!refreshed || keycloak.isTokenExpired()) {
          setIsSessionExpired(false); // Keycloak expiration, not API 401
          setShowLoginModal(true);
        } else {
          setShowLoginModal(false);
        }
      } catch (err) {
        console.error('Keycloak token refresh failed in onTokenExpired:', err);
        // Only show modal if we're still authenticated
        if (keycloak.authenticated) {
          setIsSessionExpired(false); // Keycloak expiration, not API 401
          setShowLoginModal(true);
        }
      } finally {
        refreshInProgressRef.current = false;
      }
    };

    keycloak.onTokenExpired = onTokenExpiredHandlerRef.current;

    // Set up the onAuthError event handler
    onAuthErrorHandlerRef.current = (error: any) => {
      const isIntentionalLogout = localStorage.getItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY) === 'true';
      if (!initialized || loginInProgressRef.current || isIntentionalLogout) {
        return;
      }
      console.error('Keycloak auth error:', error);
      // Only show modal if we're authenticated (not during initial auth)
      if (keycloak.authenticated) {
        setIsSessionExpired(false); // Keycloak auth error, not API 401
        setShowLoginModal(true);
      }
    };

    keycloak.onAuthError = onAuthErrorHandlerRef.current;

    // Listen for API 401 errors
    const handleApi401 = () => {
      const isIntentionalLogout = localStorage.getItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY) === 'true';
      // Only show modal if not an intentional logout and user was authenticated
      if (!isIntentionalLogout && wasAuthenticatedRef.current) {
        setIsSessionExpired(true);
        setShowLoginModal(true);
        // Clear the flag after handling
        localStorage.removeItem("api_401_detected");
      }
    };

    // Check for existing 401 flag on mount
    if (localStorage.getItem("api_401_detected") === "true") {
      handleApi401();
    }

    // Listen for api401 events
    window.addEventListener('api401', handleApi401);

    // Clean up on unmount
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      // Clean up event handlers
      if (onTokenExpiredHandlerRef.current && keycloak.onTokenExpired === onTokenExpiredHandlerRef.current) {
        keycloak.onTokenExpired = undefined;
      }
      if (onAuthErrorHandlerRef.current && keycloak.onAuthError === onAuthErrorHandlerRef.current) {
        keycloak.onAuthError = undefined;
      }
      window.removeEventListener('api401', handleApi401);
    };
  }, [initialized, keycloak]); // Only run this effect when keycloak or initialized changes

  const handleLogin = () => {
    if (keycloak) {
      loginInProgressRef.current = true;
      // Clear the intentional logout flag when user clicks login
      localStorage.removeItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY);
      // Clear session expired flag
      setIsSessionExpired(false);
      localStorage.removeItem("api_401_detected");
      keycloak.login();
    }
  };

  return [keycloak.token, showLoginModal, handleLogin, isSessionExpired];
}

export function LoginModal({ showLoginModal, handleLogin, isSessionExpired }: any) {
  const { t } = useTranslation();

  if (!showLoginModal) {
    return null;
  }

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto bg-medium-gray bg-opacity-80">
      <div className="opacity-100 flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <div className="z-60 opacity-100 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="text-center bg-white px-6 pt-8 pb-6 sm:p-8">
            {/* Gundi Logo and Name */}
            <div className="flex flex-row justify-center items-baseline mb-6">
              <img src={gundi_logo} alt="gundi logo" className="h-8" />
              <label className="ml-3 text-3xl font-bold text-black">GUNDI</label>
            </div>

            {/* Message - Show "Session Expired" if triggered by 401, otherwise show welcome message */}
            <div className="mb-6">
              {isSessionExpired ? (
                <>
                  <p className="text-lg text-dark-gray">
                    {t("Your session has expired" as any)}
                  </p>
                  <p className="text-sm text-secondary-gray mt-2">
                    {t("Please log in to continue" as any)}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg text-dark-gray">
                    {t("Welcome to Gundi" as any)}
                  </p>
                  <p className="text-sm text-secondary-gray mt-2">
                    {t("Please log in to continue" as any)}
                  </p>
                </>
              )}
            </div>

            {/* Login Button */}
            <div className="mt-6">
              <button
                type="button"
                className="font-bold w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-3 bg-dark-green text-base text-white hover:bg-status-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-green transition-colors"
                onClick={handleLogin}
              >
                {t("Log In")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}